import { useEffect, useState } from "react";
import { api, formatErr, openDataUrlInTab } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Ticket, Sparkles, Pencil, ExternalLink, Upload, Trash2, Save, X,
  Check, Clock, Globe, Star, Facebook, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/EntityPage";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ShotUpload({ label, value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max size 2MB"); return; }
    setBusy(true);
    try { const b64 = await toBase64(file); onChange(b64); }
    finally { setBusy(false); }
  }
  return (
    <div>
      <Label className="text-[10px] uppercase text-muted-foreground">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-[hsl(224_40%_12%)] hover:border-amber-500/40 text-xs">
          <Upload className="w-3.5 h-3.5"/>{value ? "Replace" : "Browse"}
          <input type="file" accept="image/*" onChange={pick} className="hidden" data-testid={testId}/>
        </label>
        {value && <img src={value} alt="" className="w-14 h-14 object-cover rounded-md border border-border"/>}
        {value && <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onChange("")}><X className="w-3.5 h-3.5"/></Button>}
        {busy && <span className="text-xs text-muted-foreground">Uploading…</span>}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">≤ 2 MB · JPG/PNG</div>
    </div>
  );
}

function ReviewLinks({ offer }) {
  const items = [
    { key: "google_review_url", label: "Google review", icon: Star, tone: "text-yellow-300 border-yellow-500/40 hover:border-yellow-400" },
    { key: "facebook_review_url", label: "Facebook review", icon: Facebook, tone: "text-sky-300 border-sky-500/40 hover:border-sky-400" },
    { key: "website_review_url", label: "Website review", icon: Globe, tone: "text-emerald-300 border-emerald-500/40 hover:border-emerald-400" },
  ].filter((it) => offer[it.key]);
  if (!items.length) return null;
  return (
    <div className="grid sm:grid-cols-3 gap-2 mt-2">
      {items.map((it) => (
        <a key={it.key} href={offer[it.key]} target="_blank" rel="noreferrer"
          data-testid={`review-${it.key}`}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-[hsl(224_40%_12%)] text-xs transition-colors ${it.tone}`}>
          <it.icon className="w-3.5 h-3.5"/> {it.label}
          <ExternalLink className="w-3 h-3 ml-auto opacity-70"/>
        </a>
      ))}
    </div>
  );
}

const CUSTOMER_TYPES = [
  { key: "premium", label: "Premium customer", tone: "text-amber-300 border-amber-500/40 bg-amber-500/5" },
  { key: "vehicle_hotel", label: "Only vehicle / hotel hire", tone: "text-sky-300 border-sky-500/40 bg-sky-500/5" },
  { key: "no_service", label: "No service customer", tone: "text-slate-300 border-slate-500/40 bg-slate-500/5" },
  { key: "not_qualified", label: "Not qualified", tone: "text-red-300 border-red-500/40 bg-red-500/5" },
];

function StarRating({ value = 0, onChange, testIdPrefix }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          data-testid={`${testIdPrefix}-${n}`}
          onClick={() => onChange(n === value ? 0 : n)}
          className="p-0.5 hover:scale-110 transition-transform"
          title={`${n} star`}>
          <Star className={`w-4 h-4 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-500"}`}/>
        </button>
      ))}
    </div>
  );
}

export default function Lottery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "subadmin";
  const isClient = user?.role === "client";

  const [offer, setOffer] = useState({});
  const [editOffer, setEditOffer] = useState(false);
  const [offerDraft, setOfferDraft] = useState({});
  const [showLinks, setShowLinks] = useState(false);

  const [entries, setEntries] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [f, setF] = useState({
    name: "", mobile: "", email: "", family_referrals: "",
    google_review_shot: "", fb_review_shot: "", fb_post_shot: "", insta_post_shot: "",
  });
  const [submitted, setSubmitted] = useState(null);

  async function loadAll() {
    const calls = [api.get("/lottery/offer")];
    if (isAdmin) calls.push(api.get("/lottery"));
    if (isClient) calls.push(api.get("/lottery/my"));
    const [o, list] = await Promise.all(calls);
    setOffer(o.data || {});
    setOfferDraft(o.data || {});
    if (isAdmin) setEntries(list?.data || []);
    if (isClient) setMyEntries(list?.data || []);
  }
  useEffect(() => { loadAll(); /* eslint-disable-line */ }, [user]);

  async function saveOffer() {
    try {
      const { data } = await api.put("/lottery/offer", offerDraft);
      setOffer(data); setEditOffer(false); toast.success("Offer updated");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  async function submit(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/lottery", f);
      setSubmitted(data);
      toast.success("Registered — awaiting admin verification");
      setF({ name: "", mobile: "", email: "", family_referrals: "",
        google_review_shot: "", fb_review_shot: "", fb_post_shot: "", insta_post_shot: "" });
      loadAll();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  async function approve(id, ok = true) {
    try {
      const { data } = await api.put(`/lottery/${id}/approve`, { approve: ok });
      toast.success(ok ? `Approved · Lottery ID ${data.lottery_id}` : "Rejected");
      loadAll();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  async function del(id) {
    if (!window.confirm("Delete entry?")) return;
    try { await api.delete(`/lottery/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error("Failed"); }
  }

  async function patchEntry(id, patch) {
    try {
      await api.patch(`/lottery/${id}`, patch);
      loadAll();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  const filteredEntries = entries.filter((r) => {
    if (nameFilter && !(r.name || "").toLowerCase().includes(nameFilter.toLowerCase())) return false;
    if (mobileFilter) {
      const digits = mobileFilter.replace(/[^0-9]/g, "");
      if (!(r.mobile || "").replace(/[^0-9]/g, "").includes(digits)) return false;
    }
    if (ratingFilter !== "all" && String(r.rating || 0) !== ratingFilter) return false;
    if (typeFilter !== "all" && (r.customer_type || "") !== typeFilter) return false;
    return true;
  });

  const typeCounts = CUSTOMER_TYPES.reduce((acc, t) => {
    acc[t.key] = entries.filter((e) => e.customer_type === t.key).length;
    return acc;
  }, {});

  const banner = offer.banner_image_url || "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <div>
      <PageHeader title="Lottery Program" subtitle="Annual international trip lottery — three-year validity." />

      {/* Approved lottery ID banner for clients */}
      {isClient && myEntries.filter((e) => e.status === "approved").map((e) => (
        <Card key={e.id} className="p-5 mb-5 bg-gradient-to-r from-emerald-500/15 to-transparent border-emerald-500/40" data-testid={`my-lottery-approved-${e.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-300 mb-2"><Check className="w-4 h-4"/><span className="text-xs uppercase tracking-[0.25em]">Registration accepted</span></div>
              <div className="font-display text-2xl font-bold">Your Lottery ID · <span className="font-mono text-emerald-300">{e.lottery_id}</span></div>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl">{e.message}</p>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Valid till {e.valid_till?.slice(0, 10)}</Badge>
          </div>
        </Card>
      ))}
      {isClient && myEntries.filter((e) => e.status === "pending").map((e) => (
        <Card key={e.id} className="p-4 mb-3 bg-amber-500/10 border-amber-500/40" data-testid={`my-lottery-pending-${e.id}`}>
          <div className="flex items-center gap-3 text-amber-200">
            <Clock className="w-4 h-4"/>
            <div className="text-sm">Your registration is awaiting admin verification. We'll notify you here and via WhatsApp / email once approved.</div>
          </div>
        </Card>
      ))}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Offer / banner */}
        <Card className="p-0 bg-[hsl(224_45%_9%)] border-border relative overflow-hidden">
          <div className="relative h-52">
            <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(224_60%_6%)] via-[hsl(224_60%_6%_/_0.5)] to-transparent"/>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 text-amber-300">
                <Sparkles className="w-4 h-4"/>
                <div className="text-[11px] uppercase tracking-[0.25em]">Annual Draw · 3-year validity</div>
              </div>
              <h2 className="font-display text-3xl font-bold mt-1 text-white drop-shadow">{offer.title}</h2>
            </div>
            {isAdmin && !editOffer && (
              <Button size="sm" variant="ghost" onClick={() => setEditOffer(true)} data-testid="edit-offer-btn"
                className="absolute top-3 right-3 bg-black/40 hover:bg-black/60 text-amber-200 gap-1 backdrop-blur">
                <Pencil className="w-3.5 h-3.5"/> Edit
              </Button>
            )}
          </div>

          <div className="p-6">
            {!editOffer ? (
              <>
                <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{offer.body}</p>
                {offer.body_secondary && (
                  <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed border-t border-border/60 pt-3" data-testid="offer-body-secondary">{offer.body_secondary}</p>
                )}
                {offer.prizes && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {offer.prizes.split("·").map((p, i) => (
                      <div key={i} className="text-xs text-emerald-200 border border-emerald-500/20 bg-emerald-500/5 rounded-md px-3 py-2">✦ {p.trim()}</div>
                    ))}
                  </div>
                )}
                {offer.hashtags && (
                  <div className="mt-4 p-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200 font-mono break-words">{offer.hashtags}</div>
                )}
                {/* Review links live here on the offer/banner side */}
                {(offer.google_review_url || offer.facebook_review_url || offer.website_review_url) && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mb-2">Share your 5-star reviews</div>
                    <ReviewLinks offer={offer}/>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div><Label className="text-[10px] uppercase text-muted-foreground">Title</Label>
                  <Input value={offerDraft.title || ""} onChange={(e) => setOfferDraft({ ...offerDraft, title: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Banner image</Label>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-[hsl(224_40%_12%)] hover:border-amber-500/40 text-xs">
                      <Upload className="w-3.5 h-3.5"/>{offerDraft.banner_image_url ? "Replace" : "Browse"}
                      <input type="file" accept="image/*" className="hidden" data-testid="offer-banner-upload"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
                          const b64 = await toBase64(f);
                          setOfferDraft({ ...offerDraft, banner_image_url: b64 });
                        }}/>
                    </label>
                    {offerDraft.banner_image_url && (
                      <>
                        <img src={offerDraft.banner_image_url} alt="" className="w-24 h-14 object-cover rounded-md border border-border"/>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOfferDraft({ ...offerDraft, banner_image_url: "" })}><X className="w-3.5 h-3.5"/></Button>
                      </>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">≤ 3 MB · JPG/PNG · shown as banner behind the offer title</div>
                </div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Offer body</Label>
                  <textarea rows={5} value={offerDraft.body || ""} onChange={(e) => setOfferDraft({ ...offerDraft, body: e.target.value })}
                    data-testid="offer-body"
                    className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Offer body (second section)</Label>
                  <textarea rows={5} value={offerDraft.body_secondary || ""} onChange={(e) => setOfferDraft({ ...offerDraft, body_secondary: e.target.value })}
                    data-testid="offer-body-secondary-input"
                    placeholder="Optional — a second paragraph shown below the main offer body (e.g. eligibility rules, timelines, or fine print)."
                    className="w-full mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border p-2 text-sm"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Prizes (separate with · )</Label>
                  <Input value={offerDraft.prizes || ""} onChange={(e) => setOfferDraft({ ...offerDraft, prizes: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Hashtags</Label>
                  <Input value={offerDraft.hashtags || ""} onChange={(e) => setOfferDraft({ ...offerDraft, hashtags: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1 font-mono text-xs"/></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Google review URL</Label>
                    <Input value={offerDraft.google_review_url || ""} onChange={(e) => setOfferDraft({ ...offerDraft, google_review_url: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Facebook URL</Label>
                    <Input value={offerDraft.facebook_review_url || ""} onChange={(e) => setOfferDraft({ ...offerDraft, facebook_review_url: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Website URL</Label>
                    <Input value={offerDraft.website_review_url || ""} onChange={(e) => setOfferDraft({ ...offerDraft, website_review_url: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" onClick={() => { setEditOffer(false); setOfferDraft(offer); }}>Cancel</Button>
                  <Button onClick={saveOffer} data-testid="save-offer-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-1"><Save className="w-3.5 h-3.5"/> Save offer</Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Registration form */}
        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="flex items-center gap-2 mb-4"><Ticket className="w-5 h-5 text-amber-400"/><h3 className="font-display text-2xl font-bold">Register for the lottery</h3></div>

          {!submitted ? null : (
            <div className="mb-4 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-sm">
              Your entry is submitted. Once admin verifies your review screenshots, you'll receive your Lottery ID here and via WhatsApp / email.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mb-2">Your details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Name</Label>
                    <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="lot-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Contact number</Label>
                    <Input required value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="lot-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                  <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Family / friend referrals (optional)</Label>
                    <Input value={f.family_referrals} onChange={(e) => setF({ ...f, family_referrals: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                </div>
              </div>

              <div className="pt-3 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mb-2">Insert screenshots of your review</div>
                <div className="grid grid-cols-2 gap-3">
                  <ShotUpload label="1. Google review browse" value={f.google_review_shot} onChange={(v) => setF({ ...f, google_review_shot: v })} testId="lot-google-shot"/>
                  <ShotUpload label="2. Facebook review browse" value={f.fb_review_shot} onChange={(v) => setF({ ...f, fb_review_shot: v })} testId="lot-fb-review-shot"/>
                  <ShotUpload label="3. Facebook post browse" value={f.fb_post_shot} onChange={(v) => setF({ ...f, fb_post_shot: v })} testId="lot-fb-post-shot"/>
                  <ShotUpload label="4. Instagram post browse" value={f.insta_post_shot} onChange={(v) => setF({ ...f, insta_post_shot: v })} testId="lot-insta-shot"/>
                </div>
              </div>

              <Button type="submit" data-testid="lot-submit" className="w-full h-11 bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Submit registration</Button>
              <div className="text-[11px] text-muted-foreground text-center">Admin will verify your data and images before you get your Lottery ID.</div>
            </form>
        </Card>
      </div>

      {/* Admin registrations */}
      {isAdmin && (
        <div className="mt-6 space-y-4">
          {/* Counting boxes */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lot-counts">
            {CUSTOMER_TYPES.map((t) => (
              <Card key={t.key} className={`p-4 border ${t.tone}`}>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t.label}</div>
                <div className="mt-1 text-2xl font-mono font-bold">{typeCounts[t.key] || 0}</div>
                <div className="text-[11px] text-muted-foreground mt-1">registered</div>
              </Card>
            ))}
            <Card className="p-4 border border-emerald-500/40 bg-emerald-500/5">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Total</div>
              <div className="mt-1 text-2xl font-mono font-bold text-emerald-300">{entries.length}</div>
              <div className="text-[11px] text-muted-foreground mt-1">registrations</div>
            </Card>
          </div>

          <Card className="bg-[hsl(224_45%_9%)] border-border">
            <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
              <div className="font-display text-lg font-bold">Lottery Registrations</div>
              <div className="text-xs text-muted-foreground">
                Pending {entries.filter((e) => e.status === "pending").length} · Approved {entries.filter((e) => e.status === "approved").length}
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Input value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name" data-testid="lot-filter-name"
                  className="w-[180px] h-8 bg-[hsl(224_40%_12%)] text-sm"/>
                <Input value={mobileFilter} onChange={(e) => setMobileFilter(e.target.value)}
                  placeholder="Filter by mobile" data-testid="lot-filter-mobile"
                  className="w-[180px] h-8 bg-[hsl(224_40%_12%)] text-sm font-mono"/>
                <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}
                  data-testid="lot-filter-rating"
                  className="h-8 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
                  <option value="all">All ratings</option>
                  {[5,4,3,2,1].map((n) => <option key={n} value={String(n)}>{n} star</option>)}
                  <option value="0">Unrated</option>
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  data-testid="lot-filter-type"
                  className="h-8 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
                  <option value="all">All types</option>
                  {CUSTOMER_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <div className="text-xs text-muted-foreground">{filteredEntries.length} of {entries.length}</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
                  {["Name","Mobile","Customer Type","Rating","Google","FB Review","FB Post","IG","Status","Lottery ID","Actions"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
                </tr></thead>
                <tbody>
                  {filteredEntries.length === 0 ? <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">No entries match</td></tr> :
                    filteredEntries.map((r) => (
                      <tr key={r.id} className="border-t border-border align-top" data-testid={`lot-row-${r.id}`}>
                        <td className="p-3">{r.name}</td>
                        <td className="p-3 font-mono text-xs">{r.mobile}</td>
                        <td className="p-3">
                          <select value={r.customer_type || ""} onChange={(e) => patchEntry(r.id, { customer_type: e.target.value })}
                            data-testid={`lot-type-${r.id}`}
                            className="h-8 rounded-md bg-[hsl(224_40%_12%)] border border-border text-xs px-2 w-[160px]">
                            <option value="">— select —</option>
                            {CUSTOMER_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                          </select>
                        </td>
                        <td className="p-3">
                          <StarRating value={r.rating || 0} onChange={(n) => patchEntry(r.id, { rating: n || 1 })} testIdPrefix={`lot-rating-${r.id}`}/>
                        </td>
                        {["google_review_shot","fb_review_shot","fb_post_shot","insta_post_shot"].map((k) => (
                          <td key={k} className="p-3">
                            {r[k] ? <button type="button" onClick={() => openDataUrlInTab(r[k])} title="Click to open"><img src={r[k]} alt="" className="w-12 h-12 object-cover rounded border border-border hover:border-amber-500/40"/></button> : <span className="text-muted-foreground">—</span>}
                          </td>
                        ))}
                        <td className="p-3">
                          {r.status === "approved" ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Approved</Badge>
                            : r.status === "rejected" ? <Badge className="bg-red-500/15 text-red-300 border-red-500/40">Rejected</Badge>
                            : <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40">Pending</Badge>}
                        </td>
                        <td className="p-3 font-mono text-xs text-emerald-300">{r.lottery_id || "—"}</td>
                        <td className="p-3">
                          <div className="inline-flex gap-1">
                            {r.status !== "approved" && (
                              <Button size="sm" onClick={() => approve(r.id, true)} data-testid={`lot-approve-${r.id}`}
                                className="h-8 bg-emerald-500/90 hover:bg-emerald-500 text-[hsl(224_60%_6%)] font-semibold gap-1">
                                <Check className="w-3.5 h-3.5"/> Approve
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-red-400" onClick={() => del(r.id)} data-testid={`del-lot-${r.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
